import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, SpinnerGap, Check, FolderSimplePlus, Images } from "@phosphor-icons/react";
import { toast } from "sonner";

// Assign a set of existing photos to one OR many albums; create a new album inline.
// When `sourceAlbumId` is given, a "move" toggle also removes them from that album.
export default function AddToAlbumDialog({ open, onOpenChange, photoIds, onDone, sourceAlbumId = null }) {
  const [albums, setAlbums] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [move, setMove] = useState(false);

  const load = () => api.get("/shared-albums")
    .then((r) => setAlbums([...(r.data.owned || []), ...(r.data.shared || [])]))
    .catch(() => setAlbums([]));

  useEffect(() => { if (open) { setPicked(new Set()); setCreating(false); setNewTitle(""); setMove(false); load(); } }, [open]);

  const toggle = (id) => setPicked((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const createAlbum = async () => {
    if (!newTitle.trim()) { toast.error("Give the album a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shared-albums", { title: newTitle.trim() });
      setAlbums((a) => [data, ...(a || [])]);
      setPicked((s) => new Set([...s, data.id]));
      setNewTitle(""); setCreating(false);
      toast.success(`Album “${data.title}” created`);
    } catch { toast.error("Could not create album"); }
    setBusy(false);
  };

  const addToAlbums = async () => {
    if (picked.size === 0) { toast.error("Pick at least one album"); return; }
    setBusy(true);
    try {
      for (const albumId of picked) {
        await api.post(`/shared-albums/${albumId}/add-photos`, { photo_ids: photoIds });
      }
      if (move && sourceAlbumId) {
        await api.post(`/shared-albums/${sourceAlbumId}/remove-photos`, { photo_ids: photoIds });
      }
      const verb = move && sourceAlbumId ? "Moved" : "Added";
      toast.success(`${verb} ${photoIds.length} photo${photoIds.length === 1 ? "" : "s"} to ${picked.size} album${picked.size === 1 ? "" : "s"}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to album");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl" data-testid="add-to-album-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <Images size={20} weight="fill" className="text-[#F27A54]" />
            Add {photoIds.length} photo{photoIds.length === 1 ? "" : "s"} to album(s)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {albums === null ? (
            <div className="grid place-items-center py-8"><SpinnerGap size={24} className="animate-spin text-[#F27A54]" /></div>
          ) : (
            <>
              <div className="max-h-64 space-y-2 overflow-y-auto pr-1" data-testid="album-picklist">
                {albums.length === 0 && !creating && (
                  <p className="py-4 text-center text-sm text-muted-foreground">No albums yet — create your first below.</p>
                )}
                {albums.map((a) => {
                  if (a.id === sourceAlbumId) return null;
                  const on = picked.has(a.id);
                  return (
                    <button key={a.id} type="button" data-testid={`album-pick-${a.id}`} onClick={() => toggle(a.id)}
                      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-[#F27A54] bg-orange-50" : "border-[#E5DFD3] hover:border-[#F27A54]/50"}`}>
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border ${on ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-zinc-300"}`}>
                        {on && <Check size={13} weight="bold" />}
                      </span>
                      {a.cover_url ? <img src={a.cover_url} alt="" className="h-9 w-9 rounded-md object-cover" /> : <span className="brand-sunset h-9 w-9 rounded-md" />}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold">{a.title}</span>
                        <span className="text-xs text-muted-foreground">{a.photo_count ?? 0} photos · {a.visibility === "public" ? "Public" : "Members"}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {creating ? (
                <div className="flex items-center gap-2">
                  <input data-testid="new-album-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createAlbum(); }} placeholder="New album name"
                    className="h-10 flex-1 rounded-xl border border-[#E5DFD3] px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" autoFocus />
                  <Button data-testid="new-album-create" onClick={createAlbum} disabled={busy} className="h-10 rounded-full bg-[#F27A54] hover:bg-[#d9633f]">
                    {busy ? <SpinnerGap size={15} className="animate-spin" /> : "Create"}
                  </Button>
                </div>
              ) : (
                <button type="button" data-testid="new-album-toggle" onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-2 rounded-xl border border-dashed border-[#E5DFD3] px-3 py-2.5 text-sm font-semibold text-[#F27A54] hover:border-[#F27A54]">
                  <FolderSimplePlus size={17} weight="fill" /> Create a new album
                </button>
              )}

              {sourceAlbumId && (
                <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-[#E5DFD3] bg-[#FAF8F5] px-3 py-2.5 text-sm" data-testid="move-toggle">
                  <input type="checkbox" checked={move} onChange={(e) => setMove(e.target.checked)}
                    className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
                  <span><span className="font-bold">Move</span> — remove from this album after adding</span>
                </label>
              )}

              <Button data-testid="add-to-album-submit" onClick={addToAlbums} disabled={busy || picked.size === 0}
                className="h-11 w-full rounded-full bg-[#2C2724] text-white hover:bg-black disabled:opacity-50">
                {busy ? <SpinnerGap size={16} className="animate-spin" /> : <><Plus size={16} weight="bold" className="mr-1" /> {move && sourceAlbumId ? "Move" : "Add"} to {picked.size || ""} album{picked.size === 1 ? "" : "s"}</>}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
